// The inline drug-interaction check for ONE medication, as the dose dialog runs it (Flutter
// getDrugInteractionInfo). Extracted from medication-dose-dialog.tsx unchanged, so a medication
// placed without the dialog — one applied from an order set (PROD-2712, AC1) — runs the SAME check
// with the SAME input: the med + the other plan meds, against the patient and the coded diagnoses.
//
// Flutter sends the new med with a fixed count of "1" (not the live dose), so the query is stable
// on the drug code. Enabled only with a patient in context and a coded drug; `useDrugInteractions`
// additionally folds in the org gate (`useIsDrugInteractionEnabled`), so while the org has the
// check off (today: the Vidal licence) nothing is fetched on either path.
import { useMemo } from 'react';
import { useDrugInteractions } from './use-drug-interactions';
import {
  buildDrugInteractionInput,
  type DrugInteractionDiagnosisInput,
  type DrugInteractionMedInput,
} from '../lib/to-drug-interaction-input';

export interface MedicationInteractionCheckArgs {
  medication: { code?: string | null; name: string; system?: string };
  patientId?: string;
  providerId?: string;
  diagnoses?: DrugInteractionDiagnosisInput[];
  /** The OTHER medications on the plan (the med being checked is prepended here). */
  existingMedications?: DrugInteractionMedInput[];
}

export function useMedicationInteractionCheck({
  medication,
  patientId,
  providerId,
  diagnoses,
  existingMedications,
}: MedicationInteractionCheckArgs) {
  const { code, name, system } = medication;
  const diInput = useMemo(
    () =>
      code
        ? buildDrugInteractionInput({
            patientId,
            providerId,
            diagnoses,
            medications: [{ code, display: name, system, quantity: '1' }, ...(existingMedications ?? [])],
          })
        : null,
    [code, name, system, patientId, providerId, diagnoses, existingMedications],
  );
  return useDrugInteractions(diInput, !!patientId && !!code);
}
